import { SessionOptions } from './session-options';
import { SessionWidgetFetchingError, SessionWidgetMissingError, SessionWidgetInitializationError } from './session-errors';

export interface SessionWidgetOptions extends SessionOptions {
  url?: string;
}

export const DEFAULT_SESSION_WIDGET_URL = 'https://gateway.jerkmate.com/wswidget/ws-session-widget.min.js';

export interface SessionWidget {
  readonly options: SessionWidgetOptions;

  fetch(): Promise<void>;
  cleanup(): Promise<void>;
  initialize(): Promise<void>;
}

export class BrowserSessionWidget implements SessionWidget {
  readonly options: SessionWidgetOptions;

  private readonly script: HTMLScriptElement = document.createElement('script');

  constructor(options: SessionWidgetOptions) {
    const { url, ...opts } = options;
    this.options = { url: url || DEFAULT_SESSION_WIDGET_URL, ...opts };
  }

  fetch(): Promise<void> {
    return new Promise((res, rej) => {
      this.script.src = this.options.url as string;
      this.script.type = 'text/javascript';
      this.script.async = true;

      const onSuccess = () => {
        res();
      };
      this.script.addEventListener('load', onSuccess);

      const onError = (err: ErrorEvent) => {
        console.error('[SESSION] Widget script failed to load:', {
          url: this.options.url,
          error: err?.message,
          event: err,
        });
        rej(new SessionWidgetFetchingError(err?.message));
        return;
      };

      this.script.addEventListener('error', onError);

      document.body.appendChild(this.script);
    });
  }

  async cleanup(): Promise<void> {
    this.script.parentElement?.removeChild(this.script);
  }

  private static readonly POLL_INTERVAL_MS = 80;
  private static readonly POLL_TIMEOUT_MS = 10_000;

  private waitForWidget(): Promise<typeof window.sessionWidget> {
    return new Promise((res, rej) => {
      const { sessionWidget } = window;
      if (typeof sessionWidget?.init === 'function') {
        res(sessionWidget);
        return;
      }

      const start = Date.now();
      const poll = () => {
        const { sessionWidget: sw } = window;
        if (typeof sw?.init === 'function') {
          res(sw);
          return;
        }
        if (Date.now() - start > BrowserSessionWidget.POLL_TIMEOUT_MS) {
          rej(new SessionWidgetMissingError());
          return;
        }
        setTimeout(poll, BrowserSessionWidget.POLL_INTERVAL_MS);
      };
      setTimeout(poll, BrowserSessionWidget.POLL_INTERVAL_MS);
    });
  }

  initialize(): Promise<void> {
    return this.waitForWidget().then(
      (widget) =>
        new Promise<void>((res, rej) => {
          widget.init(this.options, (isOk: boolean) => {
            if (isOk) {
              res();
              return;
            }
            rej(new SessionWidgetInitializationError());
          });
        }),
    );
  }
}
